import type { Lease } from '../types/leases';
import type { ParkingSpot } from '../types/parking';
import type { ParkingQueueEntry, ParkingQueueEntryInput } from '../types/parkingQueue';
import type { ParkingQueueConfig } from '../types/parkingQueueConfig';
import type { CustomerType } from '../types/customerType';
import { LeaseObjectType } from '../types/Enums';
import { leaseStatus, leaseInvolvesTenant, tenantHoldsContract, todayStockholm } from './leaseUtils';

/**
 * Pure helpers for the parking queue (spec: docs/specs/parking-queue.md).
 * No Firebase; `today` is injectable. Everything here is derived from
 * entries, leases and units at render time and never stored, except that
 * `entriesAhead` is also what the API snapshots onto a `deviation` event.
 */

export interface SpotRef {
    buildingKey: string;
    areaKey: string;
    spotKey: string;
    spot: Pick<ParkingSpot, 'type' | 'chargerComponentKey' | 'hasOutlet'>;
}

/** A parking lease the applicant holds (contract holder), with the stored label. */
export interface HeldSpot {
    leaseKey: string;
    buildingKey: string;
    label: string;
}

/** The apartment or lokal the applicant lives in / has, from their lease. */
export interface Residence {
    leaseKey: string;
    buildingKey: string;
    unitKey: string;
    /** Lease label snapshot, "Lägenhet 1201". */
    label: string;
}

export interface QueueRowFacts {
    entry: ParkingQueueEntry;
    /** Derived from leases; null when the tenant has no active or upcoming unit lease. */
    residence: Residence | null;
    /** residence or the hand-set markedInternal flag. */
    internal: boolean;
    heldSpots: HeldSpot[];
    /** Whole days from queueDate to today, never negative. */
    daysWaited: number;
    /** Stockholm datetime of the newest log event, else createdWhen. */
    lastTouched: string;
    latestNote: string | null;
}

// MARK: - Matching

/** True when the spot's area is wanted and every structured wish accepts the spot (absent wish = any). */
export const spotMatchesEntry = (entry: ParkingQueueEntry, ref: SpotRef): boolean => {
    if (entry.wantedAreas[ref.areaKey] !== ref.buildingKey) return false;
    const types = Object.keys(entry.spotTypes ?? {});
    if (types.length > 0 && !types.includes(ref.spot.type)) return false;
    const hasCharger = !!ref.spot.chargerComponentKey;
    if (entry.wantsCharger === 'yes' && !hasCharger) return false;
    if (entry.wantsCharger === 'no' && hasCharger) return false;
    const hasOutlet = ref.spot.hasOutlet === true;
    if (entry.wantsOutlet === 'yes' && !hasOutlet) return false;
    if (entry.wantsOutlet === 'no' && hasOutlet) return false;
    return true;
};

/** The wishes for a new entry added from a spot ("Lägg till i kön"): its area and type, and Ja for the charger and outlet it has; what it lacks stays open. */
export type SpotEntryPrefill = Pick<ParkingQueueEntryInput, 'wantedAreas' | 'spotTypes' | 'wantsCharger' | 'wantsOutlet'>;

export const entryPrefillForSpot = (ref: SpotRef): SpotEntryPrefill => ({
    wantedAreas: { [ref.areaKey]: ref.buildingKey },
    spotTypes: [ref.spot.type],
    wantsCharger: ref.spot.chargerComponentKey ? 'yes' : undefined,
    wantsOutlet: ref.spot.hasOutlet === true ? 'yes' : undefined,
});

// MARK: - Row facts


/** Active or upcoming parking leases anywhere where the tenant holds the contract (FR11). */
export const spotsHeld = (tenantKey: string, leases: Lease[], today: string = todayStockholm()): HeldSpot[] =>
    leases
        .filter(lease =>
            lease.object.type === LeaseObjectType.ParkingSpot &&
            tenantHoldsContract(lease, tenantKey) &&
            leaseStatus(lease, today) !== 'ended'
        )
        .map(lease => ({ leaseKey: lease.key, buildingKey: lease.buildingKey, label: lease.object.label ?? 'Parkeringsplats' }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));

export const daysBetween = (from: string, to: string): number => {
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    const ms = Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd);
    return Math.max(0, Math.round(ms / 86_400_000));
};

/** "3 dagar", "2 mån", "1 år 4 mån" — rough, for a row. */
export const describeWaited = (days: number): string => {
    if (days < 31) return `${days} ${days === 1 ? 'dag' : 'dagar'}`;
    const months = Math.round(days / (365.25 / 12));
    if (months < 12) return `${months} mån`;
    const years = Math.floor(months / 12);
    const rest = months % 12;
    return rest > 0 ? `${years} år ${rest} mån` : `${years} år`;
};

export const lastTouched = (entry: Pick<ParkingQueueEntry, 'log' | 'createdWhen'>): string => {
    let latest = entry.createdWhen;
    for (const event of entry.log) {
        if (event.when > latest) latest = event.when;
    }
    return latest;
};

export const latestNote = (entry: Pick<ParkingQueueEntry, 'log' | 'comment'>): string | null => {
    for (let i = entry.log.length - 1; i >= 0; i--) {
        const note = entry.log[i].note?.trim();
        if (note) return note;
    }
    return entry.comment?.trim() || null;
};

export const rowFacts = (entry: ParkingQueueEntry, leases: Lease[], today: string = todayStockholm()): QueueRowFacts => {
    const residence = residenceOf(entry.tenantKey, leases, today);
    return {
        entry,
        residence,
        internal: residence !== null || entry.markedInternal === true,
        heldSpots: spotsHeld(entry.tenantKey, leases, today),
        daysWaited: daysBetween(entry.queueDate, today),
        lastTouched: lastTouched(entry),
        latestNote: latestNote(entry),
    };
};

// MARK: - Ordering

export const spotsHeldGroup = (count: number): 0 | 1 | 2 => (count >= 2 ? 2 : count === 1 ? 1 : 0);

const groupRank = (row: QueueRowFacts, config: ParkingQueueConfig): number => {
    switch (config.grouping) {
        case 'internalExternal': return row.internal ? 0 : 1;
        case 'spotsHeld': return spotsHeldGroup(row.heldSpots.length);
        case 'none': return 0;
    }
};

const compareStrings = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

const sortCompare = (a: QueueRowFacts, b: QueueRowFacts, config: ParkingQueueConfig): number => {
    switch (config.sort) {
        case 'queueDate':
            return compareStrings(a.entry.queueDate, b.entry.queueDate);
        case 'spotsHeld':
            return (a.heldSpots.length - b.heldSpots.length) || compareStrings(a.entry.queueDate, b.entry.queueDate);
        case 'lastTouched':
            return compareStrings(a.lastTouched, b.lastTouched);
    }
};

/**
 * The standard order: group rank, then the configured sort, then the
 * tie-break (older createdWhen first, then key) so the order is total and
 * the deviation check is deterministic.
 */
export const compareRows = (a: QueueRowFacts, b: QueueRowFacts, config: ParkingQueueConfig): number =>
    (groupRank(a, config) - groupRank(b, config)) ||
    sortCompare(a, b, config) ||
    compareStrings(a.entry.createdWhen, b.entry.createdWhen) ||
    compareStrings(a.entry.key, b.entry.key);

export const orderRows = (rows: QueueRowFacts[], config: ParkingQueueConfig): QueueRowFacts[] =>
    [...rows].sort((a, b) => compareRows(a, b, config));

export const groupLabel = (row: QueueRowFacts, config: ParkingQueueConfig): string | null => {
    switch (config.grouping) {
        case 'internalExternal': return row.internal ? 'Interna' : 'Externa';
        case 'spotsHeld': {
            const group = spotsHeldGroup(row.heldSpots.length);
            return group === 0 ? 'Ingen plats idag' : group === 1 ? '1 plats idag' : '2+ platser idag';
        }
        case 'none': return null;
    }
};

// MARK: - Deviation

/** The active entries the spot matches, in the standard order: who is first in line for it. */
export const spotCandidates = (rows: QueueRowFacts[], ref: SpotRef, config: ParkingQueueConfig): QueueRowFacts[] =>
    orderRows(rows.filter(row => row.entry.status === 'active' && spotMatchesEntry(row.entry, ref)), config);

/**
 * The entries passed over when `entryKey` gets `ref` (FR20): candidates are
 * active entries whose wishes match the spot, in the standard order; the
 * result is the candidates strictly before the entry. Empty when the entry is
 * not itself a candidate — a mismatch is not a deviation.
 */
export const entriesAhead = (
    rows: QueueRowFacts[],
    ref: SpotRef,
    config: ParkingQueueConfig,
    entryKey: string
): QueueRowFacts[] => {
    const candidates = spotCandidates(rows, ref, config);
    const index = candidates.findIndex(row => row.entry.key === entryKey);
    return index <= 0 ? [] : candidates.slice(0, index);
};

// MARK: - Units

/**
 * Whether a new entry starts as intern (markedInternal): in a BRF most
 * applicants are members without a lease, elsewhere most are extern. A form
 * default only; the admin changes it with "Räknas som extern".
 */
export const defaultMarkedInternal = (customerType: CustomerType | null): boolean =>
    customerType === 'bostadsrattsforening';

/**
 * Where the applicant lives or has premises, derived from leases: the earliest
 * active or upcoming apartment/lokal lease the tenant is on (primary or any
 * co-tenant, medboende included — residence, not liability). Null when there
 * is none; a BRF member has no lease, which is what markedInternal is for.
 */
export const residenceOf = (
    tenantKey: string,
    leases: Lease[],
    today: string = todayStockholm()
): Residence | null => {
    const unitLease = leases
        .filter(lease =>
            (lease.object.type === LeaseObjectType.Apartment || lease.object.type === LeaseObjectType.CommercialUnit) &&
            !!lease.object.key &&
            leaseInvolvesTenant(lease, tenantKey) &&
            leaseStatus(lease, today) !== 'ended'
        )
        .sort((a, b) => compareStrings(a.startDate, b.startDate))[0];
    if (!unitLease?.object.key) return null;
    return {
        leaseKey: unitLease.key,
        buildingKey: unitLease.buildingKey,
        unitKey: unitLease.object.key,
        label: unitLease.object.label ?? (unitLease.object.type === LeaseObjectType.CommercialUnit ? 'Lokal' : 'Lägenhet'),
    };
};


// MARK: - Lease start

/**
 * Start date for a lease written from the queue: the day after the
 * spot's current lease ends when it has an end date, otherwise today.
 */
export const suggestedLeaseStart = (spotLeases: Pick<Lease, 'startDate' | 'endDate'>[], today: string = todayStockholm()): string => {
    const current = spotLeases
        .filter(lease => leaseStatus(lease, today) !== 'ended' && !!lease.endDate)
        .sort((a, b) => compareStrings(b.endDate!, a.endDate!))[0];
    if (!current?.endDate) return today;
    const [y, m, d] = current.endDate.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
};
